import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import {
  RiEyeLine,
  RiEyeOffLine,
  RiDeleteBinLine,
  RiDragMoveLine,
  RiPlayLine,
  RiEyeFill,
  RiEyeOffFill,
} from '@remixicon/react';
import {
  updateElement,
  deleteElement,
  selectElement,
  reorderElement,
  saveToHistory,
  setBackground,
} from '../../redux/slices/canvasSlice';

/**
 * Layers Panel - Manage canvas layers
 * 
 * Features:
 * - Visibility toggle
 * - Delete layer
 * - Drag to reorder (z-index)
 * - Background layer is fixed at bottom
 */
const LayersPanel = () => {
  const dispatch = useDispatch();
  const { elements, selectedElementId, background } = useSelector((state) => state.canvas);
  const [draggedElement, setDraggedElement] = useState(null);
  const [dragOverElement, setDragOverElement] = useState(null);

  const handleToggleVisibility = (elementId) => {
    const element = elements.find((el) => el.id === elementId);
    if (element) {
      dispatch(
        updateElement({
          id: elementId,
          updates: { visible: !element.visible },
        })
      );
    }
  };

  const handleDelete = (elementId) => {
    if (window.confirm('Are you sure you want to delete this layer?')) {
      dispatch(saveToHistory());
      dispatch(deleteElement(elementId));
    }
  };

  const handleSelect = (elementId) => {
    dispatch(selectElement(elementId));
  };

  // Store previous background for restore
  const [savedBackground, setSavedBackground] = useState(null);

  const handleToggleBackgroundVisibility = () => {
    dispatch(saveToHistory());
    
    // Check if currently hidden (transparent)
    const isCurrentlyHidden = background.type === 'color' && background.value === 'transparent';
    
    if (isCurrentlyHidden) {
      // Show: Restore saved background or default to white
      if (savedBackground) {
        dispatch(setBackground(savedBackground));
        setSavedBackground(null);
      } else {
        dispatch(setBackground({ type: 'color', value: '#FFFFFF' }));
      }
    } else {
      // Hide: Save current background and set to transparent
      setSavedBackground({ ...background });
      dispatch(setBackground({ type: 'color', value: 'transparent' }));
    }
  };

  const handleRemoveBackground = () => {
    if (window.confirm('Remove background and set to default white?')) {
      dispatch(saveToHistory());
      dispatch(setBackground({ type: 'color', value: '#FFFFFF' }));
      setSavedBackground(null);
    }
  };

  // Check if background is visible (not transparent)
  const isBackgroundVisible = background.type === 'color' 
    ? background.value !== 'transparent' 
    : true;

  // Drag and drop handlers
  const handleDragStart = (e, element) => {
    setDraggedElement(element);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, element) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverElement(element);
  };

  const handleDragLeave = () => {
    setDragOverElement(null);
  };

  const handleDrop = (e, targetElement) => {
    e.preventDefault();
    
    if (!draggedElement || draggedElement.id === targetElement.id) {
      setDraggedElement(null);
      setDragOverElement(null);
      return;
    }

    dispatch(saveToHistory());

    // Get current indices
    const draggedIndex = elements.findIndex((el) => el.id === draggedElement.id);
    const targetIndex = elements.findIndex((el) => el.id === targetElement.id);

    // Create new elements array with reordered items
    const newElements = [...elements];
    const [removed] = newElements.splice(draggedIndex, 1);
    newElements.splice(targetIndex, 0, removed);

    // Update z-indices based on new order
    newElements.forEach((el, idx) => {
      dispatch(updateElement({
        id: el.id,
        updates: { zIndex: idx },
      }));
    });

    setDraggedElement(null);
    setDragOverElement(null);
  };

  const handleDragEnd = () => {
    setDraggedElement(null);
    setDragOverElement(null);
  };

  // Sort elements by zIndex (highest first for display)
  const sortedElements = [...elements].sort((a, b) => b.zIndex - a.zIndex);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ padding: 2, borderBottom: '1px solid #E0E0E0' }}>
        <Typography variant="subtitle1" fontWeight={600}>
          Layers
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {elements.length} layers
        </Typography>
      </Box>

      {/* Layers List */}
      <List sx={{ padding: 0 }}>
        {sortedElements.map((element, index) => {
          const isSelected = element.id === selectedElementId;
          const isDragging = draggedElement?.id === element.id;
          const isDragOver = dragOverElement?.id === element.id;
          
          return (
            <ListItem
              key={element.id}
              draggable
              onDragStart={(e) => handleDragStart(e, element)}
              onDragOver={(e) => handleDragOver(e, element)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, element)}
              onDragEnd={handleDragEnd}
              onClick={() => handleSelect(element.id)}
              sx={{
                padding: '12px 16px',
                borderBottom: '1px solid #F5F5F5',
                backgroundColor: isSelected ? '#F0E7FF' : isDragOver ? '#E7D9FF' : 'transparent',
                cursor: isDragging ? 'grabbing' : 'grab',
                opacity: isDragging ? 0.5 : 1,
                borderTop: isDragOver ? '2px solid #6B22D5' : 'none',
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                '&:hover': {
                  backgroundColor: isSelected ? '#F0E7FF' : '#F9F9F9',
                },
              }}
            >
              {/* Visibility Toggle */}
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleToggleVisibility(element.id);
                }}
                sx={{ padding: 0.5 }}
              >
                {element.visible ? (
                  <RiEyeFill size={20} color="#171622" />
                ) : (
                  <RiEyeOffFill size={20} color="#171622" />
                )}
              </IconButton>

              {/* Layer Icon/Preview */}
              {element.type === 'shape' && element.properties.shape === 'circle' && (
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    backgroundColor: element.properties.backgroundColor || '#3B82F6',
                    border: '1px solid #E0E0E0',
                  }}
                />
              )}
              
              {element.type === 'shape' && element.properties.shape === 'rectangle' && (
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '4px',
                    backgroundColor: element.properties.backgroundColor || '#6B22D5',
                    border: '1px solid #E0E0E0',
                  }}
                />
              )}
              
              {element.type === 'shape' && element.properties.shape === 'triangle' && (
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Box
                    sx={{
                      width: 0,
                      height: 0,
                      borderLeft: '16px solid transparent',
                      borderRight: '16px solid transparent',
                      borderBottom: `28px solid ${element.properties.backgroundColor || '#10B981'}`,
                    }}
                  />
                </Box>
              )}
              
              {element.type === 'shape' && element.properties.shape === 'pentagon' && (
                <svg width="32" height="32" viewBox="0 0 32 32">
                  <polygon
                    points="16,4 28,12 24,28 8,28 4,12"
                    fill={element.properties.backgroundColor || '#F59E0B'}
                  />
                </svg>
              )}
              
              {element.type === 'text' && (
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#F5F5F5',
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="h6" sx={{ fontSize: 16, fontWeight: 600 }}>
                    T
                  </Typography>
                </Box>
              )}
              
              {element.type === 'menuItem' && (
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#FFF3E0',
                    borderRadius: 1,
                  }}
                >
                  <RiDragMoveLine size={20} />
                </Box>
              )}
              
              {element.type === 'image' && (
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 1,
                    overflow: 'hidden',
                    border: '1px solid #E0E0E0',
                  }}
                >
                  <img
                    src={element.properties.src}
                    alt="layer"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                </Box>
              )}
              
              {element.type === 'video' && (
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#E3F2FD',
                    borderRadius: 1,
                  }}
                >
                  <RiPlayLine size={20} />
                </Box>
              )}

              {/* Layer Name */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight={isSelected ? 600 : 400} noWrap>
                  {element.type === 'text'
                    ? element.properties.text?.substring(0, 20) || 'Text'
                    : element.type === 'menuItem'
                    ? element.properties.name || 'Menu Item'
                    : element.type === 'shape'
                    ? element.properties.shape.charAt(0).toUpperCase() + element.properties.shape.slice(1)
                    : element.type.charAt(0).toUpperCase() + element.type.slice(1)}
                </Typography>
              </Box>

              {/* Delete Button */}
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(element.id);
                }}
                sx={{ color: '#EF4444', padding: 0.5 }}
              >
                <RiDeleteBinLine size={20} />
              </IconButton>
            </ListItem>
          );
        })}

        {/* Background Layer (Interactive) */}
        <ListItem
          sx={{
            padding: '12px 16px',
            borderTop: '2px solid #E0E0E0',
            backgroundColor: '#F9F9F9',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            '&:hover': {
              backgroundColor: '#F0F0F0',
            },
          }}
        >
          {/* Visibility Toggle */}
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              dispatch(saveToHistory());
              handleToggleBackgroundVisibility();
            }}
            sx={{ padding: 0.5 }}
          >
            {isBackgroundVisible ? (
              <RiEyeFill size={20} color="#171622" />
            ) : (
              <RiEyeOffFill size={20} color="#171622" />
            )}
          </IconButton>

          {/* Background Preview */}
          {background.type === 'color' && (
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: 1,
                backgroundColor: background.value === 'transparent' ? '#FFFFFF' : background.value,
                border: '1px solid #E0E0E0',
                position: 'relative',
                opacity: isBackgroundVisible ? 1 : 0.3,
              }}
            >
              {background.value === 'transparent' && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundImage: 'linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%)',
                    backgroundSize: '8px 8px',
                    backgroundPosition: '0 0, 0 4px, 4px -4px, -4px 0px',
                    borderRadius: 1,
                  }}
                />
              )}
            </Box>
          )}
          
          {background.type === 'image' && (
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: 1,
                backgroundImage: `url(${background.value})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                border: '1px solid #E0E0E0',
                opacity: isBackgroundVisible ? 1 : 0.3,
              }}
            />
          )}
          
          {background.type === 'video' && (
            <Box
              sx={{
                width: 32,
                height: 32,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#E3F2FD',
                borderRadius: 1,
                opacity: isBackgroundVisible ? 1 : 0.3,
              }}
            >
              <RiPlayLine size={20} />
            </Box>
          )}

          {/* Background Label */}
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" fontWeight={500} color="text.secondary">
              {background.type === 'color'
                ? background.value === 'transparent'
                  ? 'Background (Hidden)'
                  : 'Background Color'
                : background.type === 'image'
                ? 'Background Image'
                : background.type === 'video'
                ? 'Background Video'
                : 'Background'}
            </Typography>
          </Box>

          {/* Remove/Delete Button */}
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleRemoveBackground();
            }}
            sx={{ color: '#EF4444', padding: 0.5 }}
            title="Remove background (set to white)"
          >
            <RiDeleteBinLine size={20} />
          </IconButton>
        </ListItem>
      </List>

      {/* Empty State */}
      {elements.length === 0 && (
        <Box sx={{ padding: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No layers yet. Add elements from the left toolbar.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default LayersPanel;

